import { memo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import {
  AuraLayout,
  CircularProgress,
  ElevatedCircle,
  Layout,
  WalletIcon,
} from "lola-framework-ui-test";
import {
  APP_ROUTES,
  flavorClassName,
} from "../constants";
import { detectCardBrand } from "../constants/card-brands";
import axios from "axios";
import { tokenizeCard, addCardPaymentMethod } from "../services/api";
import { paymentType } from "../interfaces/payment";
import type { AddCardFullState } from "./AddCardAddressPage";
import { useClaimLink } from "../hooks/useClaimLink";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { LINK_ERROR_CODE } from "../utils/commons";
import { safeCapture } from "../utils/posthog";

let isRegistrationInFlight = false;

export const AddCardProcessingPage = memo(() => {
  useClaimLink();
  const { t } = useTranslation();
  const {
    theme,
    cardState,
    setCardState,
    setPaymentState,
    cardList,
    setCardList,
  } = useStore();
  const { session } = useSessionStore();
  const navigate = useNavigate();
  const { redirectTo } = useRouteWithToken();
  const location = useLocation();
  const colorConfig = theme.whiteView;
  const { dropzoneColors } = colorConfig;

  const formData = location.state as AddCardFullState | undefined;

  useEffect(() => {
    if (!formData?.card || !formData?.address) {
      redirectTo(APP_ROUTES.ADD_CARD_PAGE, { replace: true });
      return;
    }

    if (isRegistrationInFlight) return;
    isRegistrationInFlight = true;

    const { card, address } = formData;

    const registerCard = async () => {
      try {
        const linkId = session.data?.linkId;
        if (!linkId) throw new Error("No linkId found in session");

        const cleanedNumber = card.cardNumber.replace(/\s/g, "");
        const brand = (detectCardBrand(cleanedNumber) || "OTHER").toUpperCase();
        const last4 = cleanedNumber.slice(-4);
        const [expiryMonth, expiryYearShort] = card.expiry.split("/");
        const expiryYear = `20${expiryYearShort}`;

        const tokenized = await tokenizeCard({
          CardNumber: cleanedNumber,
          ExpiryYear: expiryYear,
          ExpiryMonth: expiryMonth,
          CardCompany: brand,
          CardHolderName: card.cardHolderName.trim(),
          Cvv: card.cvv,
        });

        const backendCard = await addCardPaymentMethod(linkId, {
          tokenizedCardNumber: tokenized.json.CardNumber,
          tokenizedCvv: tokenized.json.Cvv,
          last4,
          brand: tokenized.json.CardCompany,
          expiryMonth: tokenized.json.ExpiryMonth,
          expiryYear: tokenized.json.ExpiryYear,
          cardholderName: tokenized.json.CardHolderName,
          billingAddress: {
            street1: address.street1.trim(),
            street2: address.street2.trim() || undefined,
            city: address.city.trim(),
            state: address.state,
            zipCode: address.zipCode.trim(),
            country: address.country || "US",
          },
        });

        const displayName = `${brand}-${last4}`;
        const cardData = {
          cardId: backendCard.paymentMethodId,
          name: displayName,
          brand,
          last4,
          accountNumber: tokenized.json.CardNumber,
          expirationDate: card.expiry,
          fullName: card.cardHolderName.trim(),
          cvv: tokenized.json.Cvv,
        };

        safeCapture("ux.send.add_card.card.registered", {
          success: true,
          brand,
          last4,
        });

        setCardState({ ...cardState, ...cardData });
        setPaymentState({
          payment: {
            cardId: backendCard.paymentMethodId,
            type: paymentType.CARD,
            brand: displayName,
          },
        });

        const cardExists = cardList.find(
          (c) => c.cardId === backendCard.paymentMethodId
        );
        if (!cardExists) setCardList([...cardList, cardData]);

        setTimeout(() => {
          redirectTo(APP_ROUTES.TRANSFER_PAGE, { replace: true });
        }, 1500);
      } catch (error) {
        console.error("Failed to tokenize / register card:", error);

        const isRateLimited =
          axios.isAxiosError(error) && error.response?.status === 429;

        const errorCode = axios.isAxiosError(error)
          ? error.response?.data?.error?.code
          : undefined;

        const isLinkNotFound =
          errorCode === LINK_ERROR_CODE.LINK_NOT_FOUND ||
          errorCode === LINK_ERROR_CODE.LINK_EXPIRED;

        safeCapture("ux.send.add_card.card.registered", {
          success: false,
          error_code: isRateLimited
            ? "RATE_LIMITED"
            : errorCode || "REGISTRATION_FAILED",
        });

        navigate(APP_ROUTES.ERROR_PAGE, {
          replace: true,
          state: isRateLimited
            ? {
              title: t("error.rateLimitTitle", {
                defaultValue: "Too Many Attempts",
              }),
              subTitle: t("error.rateLimitSubtitle", {
                defaultValue: "Please slow down",
              }),
              message: t("error.rateLimitMessage"),
              btnText: t("card.tryAgain", { defaultValue: "Try again" }),
              isOut: false,
              pageUrl: APP_ROUTES.ADD_CARD_PAGE,
            }
            : isLinkNotFound
              ? {
                title: t("error.sessionExpired"),
                message: t("error.sessionExpiredMessage"),
                isOut: true,
              }
              : {
                title: t("error.registrationFailedTitle", {
                  defaultValue: "Card Registration Failed",
                }),
                subTitle: t("error.registrationFailedSubtitle", {
                  defaultValue: "Could not add your card",
                }),
                message: t("card.registrationFailed"),
                btnText: t("transfer.tryAgain", { defaultValue: "Try again" }),
                isOut: false,
                pageUrl: APP_ROUTES.ADD_CARD_PAGE,
              },
        });
      } finally {
        isRegistrationInFlight = false;
      }
    };

    registerCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuraLayout
      colorConfig={colorConfig}
      className={`${flavorClassName}`}
    >
      <Layout.Content className="loading-section sending-money-section">
        <section className="sending-money-container">
          <CircularProgress
            size={140}
            strokeWidth={6}
            colors={dropzoneColors}
          >
            <ElevatedCircle
              shadowVariant="inset"
            >
              <WalletIcon
                size={60}
                colors={dropzoneColors}
                className="flavor-c-icon"
              />
            </ElevatedCircle>
          </CircularProgress>
          <section className="sending-caption">
            <h1 className="foreground highlight text-centered">
              {t("card.addingCard", { defaultValue: "Adding card" })}
              <span className="span-block lola-title-h1 h1">
                {t("card.pleaseWait", {
                  defaultValue: "Please wait...",
                })}
              </span>
            </h1>
          </section>
        </section>
      </Layout.Content>
    </AuraLayout>
  );
});
